import { isTimedOut, TYPING_TIMEOUT_MS } from '@clanfire/shared';
import { bootstrapApi, conversationApi, serverApi } from '@/api/endpoints';
import { isActiveTarget, isWindowFocused, navigateTo, pathForMessage } from '@/lib/navigation';
import { conversationTitle } from '@/lib/conversations';
import { maybeNotify, playChime } from '@/lib/notify';
import { acknowledge, applyChannelRead } from '@/lib/readState';
import { playSoundboard } from '@/lib/soundboard';
import { applyBootstrap, endSessionLocally } from '@/lib/session';
import { formatLater } from '@/lib/time';
import { useAuth } from '@/stores/auth';
import { useBlaze } from '@/stores/blaze';
import { useBlocks } from '@/stores/blocks';
import { keyOfMessage, targetKey, useMessages } from '@/stores/messages';
import { useTyping, useVoice } from '@/stores/realtime';
import { useEvents } from '@/stores/events';
import { useServers } from '@/stores/servers';
import { useThreads } from '@/stores/threads';
import { useGettingStarted } from '@/stores/gettingStarted';
import { useSaved } from '@/stores/saved';
import { useConversations, useFriends, useNotifications, useReadStates } from '@/stores/social';
import { toast } from '@/stores/ui';
import { usePresence, useUsers } from '@/stores/users';
import type { ClanFireSocket } from './socket';
import { voiceManager } from './voice';

const myId = () => useAuth.getState().user?.id;

/** Re-synchronises after a reconnect: anything could have happened while we were away. */
export async function resync() {
  try {
    const requestedAt = Date.now();
    applyBootstrap(await bootstrapApi.load(), requestedAt);
    useMessages.getState().invalidateAll();
    const servers = useServers.getState();
    for (const serverId of Object.keys(servers.membersState)) void servers.loadMembers(serverId, true);
    if (useFriends.getState().status === 'loaded') void useFriends.getState().load(true);
    if (useNotifications.getState().status === 'loaded') void useNotifications.getState().load();
  } catch {
    /* The next reconnect will retry. */
  }
}

function leaveViewIfIn(serverId: string) {
  if (location.pathname.startsWith(`/app/${serverId}`)) navigateTo('/app/@me');
}

const pendingServerResyncs = new Map<string, number>();

/**
 * Fetches a server again after the channels you can see may have changed (roles
 * or channel permissions). Changes come in bursts, so it waits a moment.
 */
function resyncServer(serverId: string) {
  window.clearTimeout(pendingServerResyncs.get(serverId));
  pendingServerResyncs.set(
    serverId,
    window.setTimeout(() => {
      pendingServerResyncs.delete(serverId);
      serverApi
        .get(serverId)
        .then((server) => {
          useServers.getState().upsertServer(server);
          // The channel you're looking at may have become private.
          const [, , viewedServerId, channelId] = location.pathname.split('/');
          if (viewedServerId === serverId && channelId && !server.channels.some((c) => c.id === channelId)) navigateTo(`/app/${serverId}`);
        })
        .catch(() => undefined);
    }, 250),
  );
}

export function bindHandlers(socket: ClanFireSocket) {
  /* Messages ------------------------------------------------------- */

  socket.on('message:create', ({ message }) => {
    const me = myId();
    const key = keyOfMessage(message);
    const fromMe = message.author?.id === me;
    const viewing = isActiveTarget(key);
    useMessages.getState().receive(message);
    if (message.author) useTyping.getState().stop(key, message.author.id);

    if (message.channelId) {
      useServers.getState().touchChannel(message.channelId, message.id, message.createdAt);
      if (fromMe) applyChannelRead(message.channelId, message.createdAt);
      else if (viewing && isWindowFocused()) acknowledge({ channelId: message.channelId });
    } else if (message.conversationId) {
      const conversations = useConversations.getState();
      if (!conversations.conversations[message.conversationId]) {
        // A DM that was closed (or brand new) — fetch it so it appears in the list.
        conversationApi.get(message.conversationId).then((c) => useConversations.getState().upsert(c)).catch(() => undefined);
      } else {
        conversations.touch(message.conversationId, message.id, message.createdAt, fromMe);
        if (!fromMe && viewing && isWindowFocused()) acknowledge({ conversationId: message.conversationId });
      }
    }
    maybeNotify(message, { viewing, onClick: () => navigateTo(pathForMessage(message)) });
  });

  socket.on('message:update', ({ message }) => useMessages.getState().update(message));
  socket.on('message:delete', (p) => useMessages.getState().remove(targetKey(p), p.id));
  socket.on('reaction:add', (p) => useMessages.getState().reaction(targetKey(p), p.messageId, p.emoji, p.userId, true));
  socket.on('reaction:remove', (p) => useMessages.getState().reaction(targetKey(p), p.messageId, p.emoji, p.userId, false));
  socket.on('typing:start', (p) => {
    if (p.userId !== myId()) useTyping.getState().start(targetKey(p), p.userId, p.displayName, TYPING_TIMEOUT_MS);
  });

  /* Users & presence ----------------------------------------------- */

  socket.on('presence:update', ({ userId, status }) => usePresence.getState().set(userId, status));
  socket.on('user:update', ({ user }) => useUsers.getState().upsert([user]));
  socket.on('self:update', ({ user }) => {
    useAuth.getState().setUser(user);
    useUsers.getState().upsert([user]);
  });
  socket.on('settings:update', ({ settings }) => useAuth.getState().setSettings(settings));
  // Only fetched again if the Blaze page has been opened (it fetches its own when it opens).
  socket.on('blaze:update', () => {
    if (useBlaze.getState().status) void useBlaze.getState().refresh();
  });

  /* Servers, channels, roles, members ------------------------------ */

  socket.on('server:create', ({ server }) => useServers.getState().upsertServer(server));
  socket.on('server:update', ({ server }) => useServers.getState().patchServer(server.id, server));
  socket.on('server:resync', ({ serverId }) => resyncServer(serverId));
  socket.on('server:delete', ({ serverId, reason }) => {
    const server = useServers.getState().servers[serverId];
    if (useVoice.getState().serverId === serverId) voiceManager.leave({ silent: true });
    useServers.getState().removeServer(serverId);
    leaveViewIfIn(serverId);
    if (server && reason !== 'left') {
      const verb = reason === 'deleted' ? 'was deleted' : reason === 'kicked' ? 'removed you' : 'banned you';
      toast.info(`${server.name} ${verb}.`);
    }
  });
  socket.on('channel:create', ({ channel }) => useServers.getState().upsertChannel(channel));
  socket.on('channel:update', ({ channel }) => useServers.getState().upsertChannel(channel));
  socket.on('channel:delete', ({ channelId, serverId }) => {
    useServers.getState().removeChannel(channelId);
    useVoice.getState().removeChannel(channelId);
    useThreads.getState().removeChannel(channelId);
    if (location.pathname.startsWith(`/app/${serverId}/${channelId}`)) navigateTo(`/app/${serverId}`);
  });
  socket.on('thread:create', ({ thread }) => useThreads.getState().upsert([thread]));
  socket.on('thread:update', ({ thread }) => useThreads.getState().upsert([thread]));
  socket.on('thread:delete', ({ threadId, serverId, parentId }) => {
    useThreads.getState().remove(threadId);
    useMessages.getState().forget(targetKey({ channelId: threadId }));
    if (location.pathname.endsWith(`/threads/${threadId}`)) navigateTo(`/app/${serverId}/${parentId}`);
  });
  socket.on('category:create', ({ category }) => useServers.getState().upsertCategory(category));
  socket.on('category:update', ({ category }) => useServers.getState().upsertCategory(category));
  socket.on('category:delete', ({ categoryId }) => useServers.getState().removeCategory(categoryId));
  socket.on('channels:reorder', (layout) => useServers.getState().applyLayout(layout));
  socket.on('servers:reorder', ({ serverIds }) => useServers.getState().setOrder(serverIds));
  socket.on('role:create', ({ role }) => useServers.getState().upsertRole(role));
  socket.on('role:update', ({ role }) => useServers.getState().upsertRole(role));
  socket.on('role:delete', ({ roleId, serverId }) => useServers.getState().removeRole(roleId, serverId));
  socket.on('emoji:create', ({ emoji }) => useServers.getState().upsertEmoji(emoji));
  socket.on('emoji:update', ({ emoji }) => useServers.getState().upsertEmoji(emoji));
  socket.on('emoji:delete', ({ emojiId }) => useServers.getState().removeEmoji(emojiId));
  socket.on('event:upsert', ({ event }) => {
    if (useEvents.getState().loaded[event.serverId]) useEvents.getState().upsert(event, true);
  });
  socket.on('event:delete', ({ serverId, eventId }) => useEvents.getState().remove(serverId, eventId));
  socket.on('member:join', ({ member }) => {
    const servers = useServers.getState();
    servers.upsertMember(member);
    usePresence.getState().set(member.userId, member.presence);
    const server = servers.servers[member.serverId];
    if (server && member.userId !== myId()) servers.patchServer(member.serverId, { memberCount: server.memberCount + 1 });
  });
  socket.on('member:update', ({ member }) => {
    const servers = useServers.getState();
    servers.upsertMember(member);
    const server = servers.servers[member.serverId];
    if (server && member.userId === myId()) servers.patchServer(member.serverId, { me: { ...server.me, roleIds: member.roleIds } });
  });
  socket.on('member:leave', ({ serverId, userId }) => {
    const servers = useServers.getState();
    servers.removeMember(serverId, userId);
    const server = servers.servers[serverId];
    if (server && userId !== myId()) servers.patchServer(serverId, { memberCount: Math.max(1, server.memberCount - 1) });
  });
  socket.on('membership:update', ({ serverId, notificationLevel, roleIds, nickname, timeoutUntil, rulesAcceptedAt, profile }) => {
    const server = useServers.getState().servers[serverId];
    if (!server) return;
    if (isTimedOut(timeoutUntil) && timeoutUntil !== server.me.timeoutUntil) {
      toast.info(`You've been timed out in ${server.name} until ${formatLater(timeoutUntil!)}. You can still read, but not chat.`);
    }
    useServers.getState().patchServer(serverId, { me: { ...server.me, notificationLevel, roleIds, nickname, timeoutUntil, rulesAcceptedAt, profile } });
  });

  /* Social --------------------------------------------------------- */

  socket.on('friend:request', ({ request }) => {
    useFriends.getState().addRequest(request);
    if (request.direction === 'incoming' && useAuth.getState().settings?.notificationSounds) playChime();
  });
  socket.on('friend:request-remove', ({ requestId }) => useFriends.getState().removeRequest(requestId));
  socket.on('friend:add', ({ friend, requestId }) => {
    useGettingStarted.getState().markStep('friend');
    useFriends.getState().removeRequest(requestId);
    useFriends.getState().addFriend(friend);
  });
  socket.on('friend:remove', ({ userId }) => useFriends.getState().removeFriend(userId));
  socket.on('block:update', ({ userId, blocked }) => useBlocks.getState().set(userId, blocked));
  socket.on('conversation:create', ({ conversation }) => useConversations.getState().upsert(conversation));
  socket.on('conversation:update', ({ conversation }) => useConversations.getState().applyGroupUpdate(conversation, myId()));
  socket.on('conversation:delete', ({ conversationId, reason }) => {
    const conversation = useConversations.getState().conversations[conversationId];
    useConversations.getState().remove(conversationId);
    useMessages.getState().forget(targetKey({ conversationId }));
    if (location.pathname === `/app/@me/${conversationId}`) navigateTo('/app/@me');
    if (conversation && reason === 'removed') toast.info(`You were removed from ${conversationTitle(conversation)}.`);
  });

  /* Notifications & read state ------------------------------------- */

  socket.on('saved:update', (update) => useSaved.getState().apply(update));

  // Someone in our voice channel played a soundboard sound.
  socket.on('voice:sound', ({ channelId, sound }) => {
    if (useVoice.getState().channelId === channelId) playSoundboard(sound);
  });

  socket.on('notification:new', ({ notification }) => {
    useNotifications.getState().add(notification);
    if (notification.type === 'REMINDER') toast.info(`⏰ Reminder: ${notification.preview || 'a message you saved'}`);
    if (notification.channelId && (notification.type === 'MENTION' || notification.type === 'REPLY')) {
      const viewing = isActiveTarget(targetKey({ channelId: notification.channelId }));
      if (viewing && isWindowFocused()) {
        useReadStates.getState().addMention(notification.channelId);
        acknowledge({ channelId: notification.channelId });
      } else {
        useReadStates.getState().addMention(notification.channelId);
      }
    }
  });
  socket.on('notification:read', ({ ids, channelId }) => useNotifications.getState().markRead(ids, channelId));
  socket.on('readstate:update', ({ channelId, conversationId, lastReadAt }) => {
    if (channelId) applyChannelRead(channelId, lastReadAt);
    if (conversationId) useConversations.getState().markRead(conversationId, lastReadAt);
  });
  socket.on('channel:mute', ({ channelId, muted }) => useReadStates.getState().setMuted(channelId, muted));

  /* Voice ---------------------------------------------------------- */

  socket.on('voice:participants', ({ channelId, participants }) => {
    useVoice.getState().setParticipants(channelId, participants);
    voiceManager.onParticipants(channelId, participants);
  });
  socket.on('voice:signal', ({ from, data }) => void voiceManager.onSignal(from, data));
  socket.on('voice:speaking', ({ userId, speaking }) => useVoice.getState().setSpeaking(userId, speaking));
  socket.on('voice:force-leave', ({ reason }) => voiceManager.forceLeft(reason));

  socket.on('session:revoked', () => {
    endSessionLocally();
    toast.info('You were signed out of this session.');
  });
}
